import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Shield, Zap, Users, Award, Clock, Headphones } from 'lucide-react';
import { Button } from '../../components/ui/button';

const HomeEN = () => {
  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <section className="relative overflow-hidden text-white py-20 md:py-32 px-6">
        <div className="absolute inset-0 bg-black animated-gradient"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'linear-gradient(rgba(220, 38, 38, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(220, 38, 38, 0.5) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center space-y-8">
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_07e3fa1c-accb-40af-b9f6-b18f873b4bec/artifacts/3s7e8l1h_logodas.png" 
                alt="Cloud Cheats" 
                className="h-32 md:h-48 w-auto object-contain drop-shadow-2xl"
              />
            </div>

            <h1 className="text-4xl md:text-7xl font-bold tracking-tight gaming-title">
              DOMINATE <span className="text-red-600 glow-red">FREE FIRE</span>
            </h1>
            <p className="text-lg md:text-2xl text-gray-300 max-w-3xl mx-auto font-medium">
              The only panels you can use on your main account, hassle-free and risk-free
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Link to="/en/products">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-10 py-6 text-lg font-bold border-2 border-red-500 shadow-lg shadow-red-600/50 hover:shadow-red-600/70 transition-all">
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  VIEW PRODUCTS
                </Button>
              </Link>
              <Link to="/en/feedbacks">
                <Button size="lg" variant="outline" className="border-2 border-red-600 text-white hover:bg-red-600/20 px-10 py-6 text-lg font-bold transition-all">
                  <Award className="w-5 h-5 mr-2" />
                  FEEDBACKS
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto pt-12">
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-red-600 gaming-title">500+</p>
                <p className="text-sm md:text-base text-gray-400 mt-1">Active Clients</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-red-600 gaming-title">24/7</p>
                <p className="text-sm md:text-base text-gray-400 mt-1">Online Support</p>
              </div>
              <div className="text-center">
                <p className="text-3xl md:text-4xl font-bold text-red-600 gaming-title">5.0</p>
                <p className="text-sm md:text-base text-gray-400 mt-1">Rating</p>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent"></div>
      </section>

      <section className="py-20 px-6 bg-black relative">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-center mb-4 text-white gaming-title">
            WHY CHOOSE <span className="text-red-600 glow-red">CLOUD CHEATS?</span>
          </h2>
          <p className="text-center text-gray-400 mb-16 text-lg">The best features to dominate your matches</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: '100% Safe', description: 'Advanced anti-ban system to protect your account during all use', color: 'from-red-600 to-red-800' },
              { icon: Zap, title: 'Instant Updates', description: 'Always updated with the latest Free Fire versions and patches', color: 'from-red-600 to-orange-600' },
              { icon: Headphones, title: 'Premium 24/7 Support', description: 'Specialized team ready to help you at any time', color: 'from-red-600 to-pink-600' },
              { icon: Users, title: 'Active Community', description: 'Join a community of elite players', color: 'from-orange-600 to-red-600' },
              { icon: Clock, title: 'Instant Delivery', description: 'Receive your access immediately after payment confirmation', color: 'from-pink-600 to-red-600' },
              { icon: Award, title: 'Quality Guarantee', description: 'Panels tested and approved by thousands of players', color: 'from-red-800 to-red-600' }
            ].map((feature, idx) => (
              <div 
                key={idx} 
                className="bg-gray-800 p-8 rounded-xl border border-red-900/30 hover:border-red-600/50 transition-all card-premium group hover:transform hover:scale-105"
              >
                <div className={`bg-gradient-to-br ${feature.color} w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg group-hover:shadow-red-600/50 transition-shadow`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white gaming-title">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-6 bg-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle, rgba(220, 38, 38, 0.3) 1px, transparent 1px)',
          backgroundSize: '30px 30px'
        }}></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-white gaming-title glow-red">
            READY TO DOMINATE?
          </h2>
          <p className="text-lg md:text-xl text-gray-300 mb-10">
            Join thousands of players already at the top of the rankings
          </p>
          <Link to="/en/products">
            <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-12 py-7 text-xl font-bold border-2 border-red-500 shadow-2xl shadow-red-600/50 hover:shadow-red-600/80 transition-all gaming-title">
              <ShoppingCart className="w-6 h-6 mr-3" />
              BUY NOW
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomeEN;
